import { useEffect, useId, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type FocusableElement = HTMLElement | SVGElement;

const isVisible = (element: FocusableElement): boolean =>
  element instanceof HTMLElement ? element.offsetParent !== null : element.getClientRects().length > 0;

export const getFocusableElements = (container: HTMLElement): FocusableElement[] =>
  Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element): element is FocusableElement =>
      (element instanceof HTMLElement || element instanceof SVGElement) &&
      !element.hasAttribute('disabled') &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.closest('[inert]') === null &&
      isVisible(element)
  );

// Module-level stack of currently-active traps. Used so the focus trap only
// applies to the topmost dialog — without this, two open dialogs fight over the
// document keydown handler and the lower one yanks focus out of the upper one.
const openModalStack: string[] = [];
const isTopmost = (id: string) => openModalStack[openModalStack.length - 1] === id;

type UseFocusTrapParams = {
  active: boolean;
  getContainer: () => HTMLElement | null;
};

/**
 * Traps Tab focus inside the container while active: saves the previously focused
 * element, moves focus into the container on activation, wraps Tab/Shift+Tab at the
 * edges, and restores focus on deactivation. Traps stack — only the topmost active
 * one handles Tab — so nested dialogs compose.
 *
 * The container must be able to receive focus itself (tabIndex={-1}) as the fallback
 * target when it holds no focusable elements.
 */
export function useFocusTrap({ active, getContainer }: UseFocusTrapParams) {
  const trapId = useId();
  const previousFocusRef = useRef<FocusableElement | null>(null);
  const getContainerRef = useRef(getContainer);
  getContainerRef.current = getContainer;

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const { activeElement } = document;

    if (activeElement instanceof HTMLElement || activeElement instanceof SVGElement) {
      previousFocusRef.current = activeElement;
    }

    openModalStack.push(trapId);

    const focusInitialElement = () => {
      if (!isTopmost(trapId)) return;

      const container = getContainerRef.current();

      if (!container) {
        return;
      }

      const focusableElements = getFocusableElements(container);
      const elementToFocus = focusableElements[0] ?? container;
      elementToFocus.focus();
    };

    const animationFrameId = window.requestAnimationFrame(focusInitialElement);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') {
        return;
      }

      // Only the topmost dialog traps Tab — otherwise a lower dialog yanks focus
      // back to itself while the user is interacting with the stacked one.
      if (!isTopmost(trapId)) return;

      const container = getContainerRef.current();

      if (!container) {
        return;
      }

      const focusableElements = getFocusableElements(container);
      const firstElement = focusableElements[0] ?? container;
      const lastElement = focusableElements[focusableElements.length - 1] ?? container;
      const currentActiveElement = document.activeElement;

      if (!container.contains(currentActiveElement)) {
        e.preventDefault();
        (e.shiftKey ? lastElement : firstElement).focus();
        return;
      }

      if (e.shiftKey && currentActiveElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }

      if (!e.shiftKey && currentActiveElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      document.removeEventListener('keydown', handleKeyDown);

      const idx = openModalStack.lastIndexOf(trapId);
      if (idx !== -1) openModalStack.splice(idx, 1);

      if (previousFocusRef.current?.isConnected && document.activeElement !== previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, [trapId, active]);
}
